import React from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

export interface SegmentedControlProps<T extends string> {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  /** Names the choice for screen readers, e.g. "Measure". */
  ariaLabel: string;
  disabled?: boolean;
}

/**
 * Two to four short choices side by side, one always picked: Cases |
 * Premium, Weekly | Monthly.
 *
 * Every option is visible, so switching is one click and the current choice
 * is obvious. For longer lists, or options that need explaining, use
 * `SelectMenu`.
 */
export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  disabled,
}: SegmentedControlProps<T>) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(_, next: T | null) => {
        if (next) onChange(next);
      }}
      sx={(t) => ({
        p: 0.25,
        gap: 0.25,
        borderRadius: `${t.ds.radius.sm}px`,
        border: `1px solid ${t.tokens.border.default}`,
        bgcolor: t.tokens.surface.raised,
        '& .MuiToggleButton-root': {
          ...t.typography.body2,
          height: t.ds.sizes.control.sm - 6,
          px: 1.25,
          border: 0,
          borderRadius: `${t.ds.radius.xs}px !important`,
          color: t.tokens.text.secondary,
          fontWeight: 500,
          textTransform: 'none',
          '&:hover': { bgcolor: t.tokens.brand.primarySofter },
          '&.Mui-selected, &.Mui-selected:hover': {
            bgcolor: t.tokens.brand.primarySoft,
            color: t.tokens.text.accent,
            fontWeight: 600,
          },
          '&.Mui-focusVisible': { outline: `2px solid ${t.tokens.border.focus}`, outlineOffset: 1 },
        },
      })}
    >
      {options.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
